import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './styles/globals.css';

// Google Analytics (optional - only if you want analytics)
import { initGA, logPageView } from './utils/analytics';

// Hooks
import { useCart } from './hooks/useCart';

// Layout Components
import Navbar from './components/layout/Navbar';
import MobileMenu from './components/layout/MobileMenu';
import Footer from './components/layout/Footer';

// Section Components
import HeroSection from './components/sections/HeroSection';
import CatalogSection from './components/sections/CatalogSection';
import FeaturesSection from './components/sections/FeaturesSection';

// Other Components
import CategoryFilter from './components/product/CategoryFilter';
import CartDrawer from './components/cart/CartDrawer';
import AdminPanel from './components/admin/AdminPanel';
import ProtectedRoute from './components/admin/ProtectedRoute';

// Import Firebase functions
import { getProducts } from './data/storeData';

// Import Cookie Consent
import CookieConsent from './components/common/CookieConsent';

// Enhanced Styles (animations)
const enhancedStyles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(60px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
    100% { transform: translateY(0px); }
  }

  @keyframes floatIcon {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  
  @keyframes floatHorizontal {
    0% { transform: translateX(0px) translateY(0px); }
    33% { transform: translateX(20px) translateY(10px); }
    66% { transform: translateX(-10px) translateY(-5px); }
    100% { transform: translateX(0px) translateY(0px); }
  }

  @keyframes pulseGlow {
    0% { opacity: 0.1; transform: scale(1); }
    50% { opacity: 0.2; transform: scale(1.1); }
    100% { opacity: 0.1; transform: scale(1); }
  }

  @keyframes reduceWhite {
    0% { color: #ffffff; filter: drop-shadow(0 0 10px rgba(255,255,255,0.8)); }
    100% { color: #d1d5db; filter: drop-shadow(0 0 0px rgba(255,255,255,0)); } 
  }

  @keyframes breathe {
    0%, 100% { opacity: 0.8; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
  }

  .reveal-base {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1), transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
  
  .reveal-active {
    opacity: 1;
    transform: translateY(0);
  }

  .animate-float-slow { animation: float 8s ease-in-out infinite; }
  .animate-float-medium { animation: float 6s ease-in-out infinite; animation-delay: 1s; }
  .animate-float-drift { animation: floatHorizontal 12s ease-in-out infinite; }
  .animate-pulse-slow { animation: pulseGlow 5s ease-in-out infinite; }
  .animate-reduce-white { animation: reduceWhite 3s ease-out forwards; animation-delay: 1s; }
  .animate-breathe { animation: breathe 4s ease-in-out infinite; }
  
  .animate-icon-1 { animation: floatIcon 4s ease-in-out infinite; animation-delay: 0s; }
  .animate-icon-2 { animation: floatIcon 5s ease-in-out infinite; animation-delay: 1.5s; }
  .animate-icon-3 { animation: floatIcon 4.5s ease-in-out infinite; animation-delay: 0.5s; }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  body {
    font-family: "SF Pro Text", "SF Pro Icons", "Helvetica Neue", "Helvetica", "Arial", sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

// Main Store Component
function MainStore() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false); // ← MOVED HERE!
  
  const { cart, isCartOpen, setIsCartOpen, addToCart, updateQuantity } = useCart();
  const location = useLocation();

  // ============================================
  // GOOGLE ANALYTICS SETUP
  // ============================================
  
  // Check for previous consent
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (consent === 'accepted') {
      const GA_MEASUREMENT_ID = 'G-791DERFSQW'; // YOUR ID
      initGA(GA_MEASUREMENT_ID);
      setAnalyticsEnabled(true);
      console.log('Google Analytics initialized from previous consent');
    }
  }, []);

  // Track page views when route changes (only if analytics enabled)
  useEffect(() => {
    if (analyticsEnabled) {
      logPageView(location.pathname);
    }
  }, [location, analyticsEnabled]);
  
  // ============================================
  // END GOOGLE ANALYTICS SETUP
  // ============================================

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load products from Firebase (async)
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const prods = await getProducts();
        // Sort by order field
        const sorted = (prods || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        setProducts(sorted);
      } catch (error) {
        console.error('Error loading products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadProducts();
    
    // Listen for product updates from admin panel
    const handleProductUpdate = () => loadProducts();
    window.addEventListener('productsUpdated', handleProductUpdate);
    
    return () => {
      window.removeEventListener('productsUpdated', handleProductUpdate);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] selection:bg-[#0071e3] selection:text-white [overflow-x:clip]">
      <style>{enhancedStyles}</style>
      
      <Navbar 
        isScrolled={isScrolled}
        cart={cart}
        onCartOpen={() => setIsCartOpen(true)}
        onMobileMenuOpen={() => setIsMobileMenuOpen(true)}
        setActiveCategory={setActiveCategory}
      />

      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
        setActiveCategory={setActiveCategory}
      />

      <HeroSection />

      <CategoryFilter 
        activeCategory={activeCategory} 
        setActiveCategory={setActiveCategory} 
      />

      {loading ? (
        <div className="min-h-screen bg-[#f5f5f7] py-24">
          <div className="container mx-auto px-6">
            {/* Skeleton header */}
            <div className="flex flex-col items-center mb-16">
              <div className="h-3 w-28 bg-gray-200 rounded-full animate-pulse mb-5" />
              <div className="h-10 w-72 md:w-96 bg-gray-200 rounded-2xl animate-pulse" />
            </div>
            {/* Skeleton cards */}
            <div className="flex gap-6 overflow-hidden">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-[300px] md:w-[340px] flex-shrink-0 bg-white rounded-[1.75rem] p-7 border border-gray-100 ${i > 0 ? 'hidden sm:block' : ''} ${i > 2 ? 'hidden xl:block' : ''}`}
                >
                  <div className="w-full aspect-[4/5] bg-gray-100 rounded-2xl animate-pulse mb-6" />
                  <div className="h-5 w-3/4 bg-gray-100 rounded-full animate-pulse mx-auto mb-3" />
                  <div className="h-4 w-1/2 bg-gray-100 rounded-full animate-pulse mx-auto mb-8" />
                  <div className="flex items-center justify-between">
                    <div className="h-6 w-16 bg-gray-100 rounded-full animate-pulse" />
                    <div className="h-11 w-24 bg-gray-100 rounded-full animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <CatalogSection 
          activeCategory={activeCategory}
          onAddToCart={addToCart}
          products={products}
        />
      )}

      <FeaturesSection />

      <Footer />

      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={updateQuantity}
      />

      {/* Cookie Consent Banner - MOVED TO CORRECT POSITION */}
      <CookieConsent 
        onAccept={() => {
          const GA_MEASUREMENT_ID = 'G-791DERFSQW'; // YOUR ID
          initGA(GA_MEASUREMENT_ID);
          setAnalyticsEnabled(true);
          console.log('Google Analytics enabled by user');
        }}
        onReject={() => {
          setAnalyticsEnabled(false);
          console.log('Google Analytics disabled by user');
        }}
      />
    </div>
  );
}

// Main App with Routing
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainStore />} />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;